import {Place} from '@/interfaces/place.interface';
import {GoogleTravelModesEnum} from '@/classes/api/google/GoogleTravelModes.enum';
import {AcquisitionListStatusEnum} from '@/enums/institutioner/acquisition/AcquisitionListStatus.enum';
import {SchoolRow} from './schoolRow';

const EARTH_RADIUS_M = 6371000;

/** Air-line distance in meters between two places (GeoJSON coordinates are [lng, lat]) */
export function airDistance(a?: Place | null, b?: Place | null): number | null {
    const ca = a?.location?.coordinates;
    const cb = b?.location?.coordinates;
    if (!ca || !cb) {
        return null;
    }
    const toRad = (value: number) => value * Math.PI / 180;
    const dLat = toRad(cb[1] - ca[1]);
    const dLng = toRad(cb[0] - ca[0]);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(ca[1])) * Math.cos(toRad(cb[1])) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

export function formatKm(meters?: number | null): string {
    if (meters === null || meters === undefined) {
        return '–';
    }
    const km = meters / 1000;
    return `${km.toLocaleString('de-DE', {maximumFractionDigits: km < 10 ? 1 : 0})} km`;
}

export function formatMinutes(seconds?: number | null): string {
    if (!seconds) {
        return '–';
    }
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) {
        return `${minutes} Min`;
    }
    return `${Math.floor(minutes / 60)} Std ${minutes % 60} Min`;
}

export function isAddressComplete(place?: Place | null): boolean {
    return !!(place?.street && place.houseNumber && place.postal && place.city && place.location?.coordinates);
}

/** Labels of the address parts, each with whether it is present */
export function addressParts(place?: Place | null): Array<{ label: string; ok: boolean }> {
    return [
        {label: 'Straße', ok: !!place?.street},
        {label: 'Hausnummer', ok: !!place?.houseNumber},
        {label: 'PLZ', ok: !!place?.postal},
        {label: 'Ort', ok: !!place?.city},
    ];
}

export const TRAVEL_MODES = [
    {value: GoogleTravelModesEnum.DRIVING, text: 'Auto', icon: 'mdi-car', noun: 'mit dem Auto'},
    {value: GoogleTravelModesEnum.TRANSIT, text: 'ÖPNV', icon: 'mdi-bus', noun: 'mit Bus und Bahn'},
    {value: GoogleTravelModesEnum.BICYCLING, text: 'Fahrrad', icon: 'mdi-bicycle', noun: 'mit dem Fahrrad'},
    {value: GoogleTravelModesEnum.WALKING, text: 'Zu Fuß', icon: 'mdi-walk', noun: 'zu Fuß'},
];

export type ChangedPreset = 'any' | '6m' | '12m' | 'range';

export const CHANGED_PRESETS: Array<{ value: ChangedPreset; text: string }> = [
    {value: 'any', text: 'egal'},
    {value: '6m', text: '> 6 Mon.'},
    {value: '12m', text: '> 12 Mon.'},
    {value: 'range', text: 'Zeitraum'},
];

/** Maps the preset of "status last changed" to the existing filter `lastUpdatedAcquisitionStatusBetween` */
export function changedFilter(preset: ChangedPreset, start?: Date | null, end?: Date | null): { start: Date; end?: Date } | undefined {
    const monthsAgo = (months: number) => {
        const date = new Date();
        date.setMonth(date.getMonth() - months);
        return date;
    };
    switch (preset) {
        case '6m':
            return {start: new Date(0), end: monthsAgo(6)};
        case '12m':
            return {start: new Date(0), end: monthsAgo(12)};
        case 'range':
            return start ? {start, end: end || undefined} : undefined;
        default:
            return undefined;
    }
}

export const DEFAULT_STATUSES = [AcquisitionListStatusEnum.OPEN, AcquisitionListStatusEnum.POTENTIAL];

export function relativeMonths(date?: string | Date | null): string {
    if (!date) {
        return 'noch nie geändert';
    }
    const months = Math.floor((Date.now() - new Date(date).getTime()) / (30.44 * 24 * 3600 * 1000));
    if (months < 1) {
        return 'diesen Monat geändert';
    }
    return months === 1 ? 'vor 1 Monat' : `vor ${months} Monaten`;
}

export function daysSince(date?: string | Date | null): number | null {
    if (!date) {
        return null;
    }
    return Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / (24 * 3600 * 1000)));
}

/** Sort time for "Neueste zuerst": newest application (newestApplicationAt), else createdAt (older institutioner: waitingSince) */
export function newestTime(candidate: { newestApplicationAt?: string | Date | null; waitingSince?: string | Date | null; employee: { createdAt?: string | Date } }): number {
    const date = candidate.newestApplicationAt !== undefined ? candidate.newestApplicationAt || candidate.employee.createdAt
        : candidate.waitingSince || candidate.employee.createdAt;
    return date ? new Date(date).getTime() : 0;
}

export type SchoolSortKey = 'distance' | 'name' | 'changed';

/** Result row of the radius search; a missing status counts as "Offen" (like everywhere else in the views) */
export function toSchoolRow(institution: any, home: Place | null | undefined, fromApplication: Set<string>): SchoolRow {
    return {
        _id: institution._id,
        name: institution.name || '',
        address: institution.address,
        acquisitionStatus: institution.acquisitionStatus ?? AcquisitionListStatusEnum.OPEN,
        lastUpdatedAcquisitionStatus: institution.lastUpdatedAcquisitionStatus,
        isSelected: true,
        airDistance: airDistance(home, institution.address),
        fromApplication: fromApplication.has(String(institution._id)),
    };
}

/** Rows of the list: search term, "Aus Bewerbung" and sorting */
export function listSchoolRows(rows: SchoolRow[], options: { term?: string; onlyApplication: boolean; sortBy: SchoolSortKey; exact: boolean }): SchoolRow[] {
    const term = (options.term || '').trim().toLowerCase();
    let result = options.onlyApplication ? rows.filter((row) => row.fromApplication) : [...rows];
    if (term) {
        result = result.filter((row) => `${row.name} ${row.address?.city || ''} ${row.address?.street || ''}`.toLowerCase().includes(term));
    }
    const last = Number.MAX_SAFE_INTEGER;
    switch (options.sortBy) {
        case 'name':
            return result.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de'));
        case 'changed':
            return result.sort((a, b) => new Date(a.lastUpdatedAcquisitionStatus || 0).getTime() - new Date(b.lastUpdatedAcquisitionStatus || 0).getTime());
        default:
            return result.sort((a, b) => options.exact
                ? (a.duration ?? last) - (b.duration ?? last)
                : (a.airDistance ?? last) - (b.airDistance ?? last));
    }
}

export type RouteInfo = { distance: number; duration: number };

/** Rows with driving distance/time of the current travel mode, outside flag and selection (deselected, outside only when picked manually) */
export function withRoutes(rows: SchoolRow[], info: Record<string, RouteInfo | null>, options: {
    isArea: boolean; radiusKm: number; deselected: Record<string, boolean>; manualOutside: Record<string, boolean>;
}): SchoolRow[] {
    const maxMeters = options.radiusKm * 1000;
    return rows.map((row) => {
        const route = info[String(row._id)];
        // Drawn area: outside means outside the area (comparison radius); route distances are only shown
        const outside = options.isArea ? !!row.outsideArea : !!route && route.distance > maxMeters;
        return {
            ...row,
            distance: route?.distance,
            duration: route?.duration,
            outside,
            isSelected: !options.deselected[String(row._id)] && !(outside && !options.manualOutside[String(row._id)]),
        };
    });
}
